import { api } from '../api';

/**
 * As chamadas dos ensaios triviais — umidade, LL e LP.
 *
 * Os caminhos vêm escritos por inteiro, e não montados a partir de um
 * sufixo, pela razão que o teste `26` cobra: a prova de que existe
 * caminho da tela até a rota não pode depender de eu afirmar que existe.
 * Quem procurar `/limite-liquidez/pontos` no projeto acha esta linha.
 *
 * Todas devolvem o ENSAIO INTEIRO, recalculado pelo banco. Gravar uma
 * cápsula muda a média, muda o LL, muda a lista de pendências — e uma
 * tela que remontasse esse estado sozinha passaria a ter uma segunda
 * opinião sobre o ensaio (D-29).
 */

/** A balança da bateria (RN-215, onda 236) — uma por bateria. */
export interface BalancaDoEnsaio {
  id: string;
  codigo: string;
  nome: string;
}

export interface EnsaioUmidade {
  cabecalho: any;
  balanca: BalancaDoEnsaio | null;
  /** A hora do servidor — o relógio das janelas de correção (onda 290). */
  agora?: string;
  pendenciasDetalhadas?: any[];
  determinacoes: any[];
  equipamentos: any[];
  pendencias: string[];
  /** A tentativa aberta pela repetição (onda 323), ou `null`. */
  repeticaoAberta?: number | null;
}

export interface EnsaioLimiteLiquidez {
  cabecalho: any;
  balanca: BalancaDoEnsaio | null;
  agora?: string;
  telas?: string[];
  pendenciasDetalhadas?: any[];
  pontos: any[];
  par: any;
  pendencias: string[];
  /** A tentativa aberta pela repetição (onda 323), ou `null`. */
  repeticaoAberta?: number | null;
}

export interface EnsaioLimitePlasticidade {
  cabecalho: any;
  balanca: BalancaDoEnsaio | null;
  agora?: string;
  telas?: string[];
  pendenciasDetalhadas?: any[];
  determinacoes: any[];
  par: any;
  pendencias: string[];
  /** A tentativa aberta pela repetição (onda 323), ou `null`. */
  repeticaoAberta?: number | null;
}

// ── umidade ───────────────────────────────────────────────────────────

export const verUmidade = (id: string) =>
  api<EnsaioUmidade>(`/ensaios/${id}/umidade`);

export const gravarUmidadeCabecalho = (id: string, corpo: unknown) =>
  api<EnsaioUmidade>(`/ensaios/${id}/umidade`, { metodo: 'PUT', corpo });

export const gravarUmidadeDeterminacoes = (id: string, corpo: unknown) =>
  api<EnsaioUmidade>(`/ensaios/${id}/umidade/determinacoes`,
    { metodo: 'POST', corpo });

export const descartarUmidadeDeterminacao = (
  id: string, did: string, motivo: string,
) =>
  api<EnsaioUmidade>(`/ensaios/${id}/umidade/determinacoes/${did}/descartar`,
    { metodo: 'POST', corpo: { motivo } });

// ── limite de liquidez ────────────────────────────────────────────────

export const verLimiteLiquidez = (id: string) =>
  api<EnsaioLimiteLiquidez>(`/ensaios/${id}/limite-liquidez`);

export const gravarLlCabecalho = (id: string, corpo: unknown) =>
  api<EnsaioLimiteLiquidez>(`/ensaios/${id}/limite-liquidez`,
    { metodo: 'PUT', corpo });

export const gravarLlPontos = (id: string, corpo: unknown) =>
  api<EnsaioLimiteLiquidez>(`/ensaios/${id}/limite-liquidez/pontos`,
    { metodo: 'POST', corpo });

export const descartarLlPonto = (id: string, pid: string, motivo: string) =>
  api<EnsaioLimiteLiquidez>(
    `/ensaios/${id}/limite-liquidez/pontos/${pid}/descartar`,
    { metodo: 'POST', corpo: { motivo } });

// ── limite de plasticidade ────────────────────────────────────────────

export const verLimitePlasticidade = (id: string) =>
  api<EnsaioLimitePlasticidade>(`/ensaios/${id}/limite-plasticidade`);

export const gravarLpCabecalho = (id: string, corpo: unknown) =>
  api<EnsaioLimitePlasticidade>(`/ensaios/${id}/limite-plasticidade`,
    { metodo: 'PUT', corpo });

export const gravarLpDeterminacoes = (id: string, corpo: unknown) =>
  api<EnsaioLimitePlasticidade>(
    `/ensaios/${id}/limite-plasticidade/determinacoes`,
    { metodo: 'POST', corpo });

export const descartarLpDeterminacao = (
  id: string, did: string, motivo: string,
) =>
  api<EnsaioLimitePlasticidade>(
    `/ensaios/${id}/limite-plasticidade/determinacoes/${did}/descartar`,
    { metodo: 'POST', corpo: { motivo } });

/**
 * A interpretação do LL (RN-238, onda 290) — só para quem tem
 * `ensaio.interpretar`.
 */
export const gravarInterpretacaoLl = (id: string, corpo: unknown) =>
  api<{ ok: boolean }>(`/ensaios/${id}/limite-liquidez/interpretacao`,
    { metodo: 'PUT', corpo });

// ── a estufa: a pesagem seca e a correção (onda 290) ───────────────────

/**
 * A pesagem seca — a MESMA rota para a ficha e para a tela Estufa.
 *
 * É isso que impede a duplicata: o servidor só grava se a linha ainda não
 * foi pesada seca, e a segunda tentativa ouve quem pesou e quando (RN-235).
 */
export interface RespostaDaPesagem {
  linha: any;
  contexto: { pontos: any[]; aguardando: number; alertas: string[] };
}

export const pesarSeca = (corpo: {
  linhaId: string; massaCapsulaSoloSeco: number; balancaEquipamentoId?: string;
}) => api<RespostaDaPesagem>('/estufa/pesagens', { metodo: 'POST', corpo });

/** Corrigir uma pesagem gravada (RN-236): a trava mora no banco. */
export const corrigirLinha = (corpo: {
  linhaId: string; capsulaEquipamentoId?: string; golpes?: number;
  massaCapsulaSoloUmido?: number; massaCapsulaSoloSeco?: number;
  balancaEquipamentoId?: string; motivo?: string;
}) => api<RespostaDaPesagem>('/estufa/correcoes', { metodo: 'POST', corpo });

export interface Estufa {
  naEstufa: any[];
  recentes: any[];
  resumo: { na_estufa: number; ensaios: number; entraram_hoje: number;
    entraram_antes: number; pesadas_hoje: number; em_alerta?: number } | null;
  agora: string;
  janelaMin: number;
  /** Depois de quantas horas a cápsula entra em alerta (RN-250, onda 305). */
  alertaHoras?: number;
}

export const verEstufa = () => api<Estufa>('/estufa');

/**
 * As linhas de cápsula de UM ensaio, de qualquer das nove tabelas (onda
 * 294, RN-241) — o carimbo das duas pesagens e se a cápsula está na estufa.
 */
export interface EstufaDoEnsaio {
  linhas: any[];
  agora: string;
  janelaMin: number;
  /** Depois de quantas horas a cápsula entra em alerta (RN-250, onda 305). */
  alertaHoras?: number;
}

export const verEstufaDoEnsaio = (ensaioId: string) =>
  api<EstufaDoEnsaio>(`/estufa/ensaio/${ensaioId}`);

// ── os ajustes do sistema (onda 290) ──────────────────────────────────

export const verAjustes = () => api<any[]>('/ajustes');

export const alterarAjuste = (chave: string, corpo: { valor: number; motivo: string }) =>
  api<any[]>(`/ajustes/${chave}`, { metodo: 'PUT', corpo });

// ── o par da amostra ──────────────────────────────────────────────────

export const verLimitesDaAmostra = (amostraId: string) =>
  api<any>(`/amostras/${amostraId}/limites`);
